import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Decision } from '@mas-flow/types';
import { useCreateItem, useItem, usePatchItem, useAnchorItem, useArchiveItem, useItems, useRankItem } from '../hooks/useItems';
import { useCategories } from '../hooks/useCategories';
import { useCan } from '../auth/AuthProvider';
import { useAuth } from '../auth/useAuth';
import { ApiHttpError } from '../api/client';
import { ScoringBlocks } from './ScoringBlocks';
import { DefinitionPopover } from './DefinitionPopover';
import { VoteDistribution } from './VoteDistribution';
import { getDefinition } from '../content/definitions';
import { AnchorReferencePanel } from './AnchorReferencePanel';
import { ActivityTimeline } from './ActivityTimeline';
import { ConflictBanner } from './ConflictBanner';
import { useCastVote } from '../hooks/useVote';

export function ItemDrawer() {
  const [params, setParams] = useSearchParams();
  const itemId = params.get('item');
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('item');
    setParams(next);
  };

  useEffect(() => {
    if (!itemId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!itemId) return null;
  if (itemId === 'new') return <CreateDrawer onClose={close} />;
  return <EditDrawer itemId={itemId} onClose={close} />;
}

function EditDrawer({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const { data: item, refetch } = useItem(itemId);
  const { data: allItems } = useItems();
  const patch = usePatchItem();
  const setAnchor = useAnchorItem();
  const archive = useArchiveItem();
  const rank = useRankItem();
  const [conflict, setConflict] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  // useCan calls must be unconditional (Rules of Hooks) — item arg is unused by
  // the current permissions matrix so omitting it here is safe.
  const canEditIntake = useCan('item.editIntake');
  const canScore = useCan('item.score');
  const canFlagAnchor = useCan('item.flagAnchor');
  const canArchive = useCan('item.archive');
  const canSetDecision = useCan('item.setDecision');
  const { user } = useAuth();
  const principalUserId = user?.userId ?? '_anon';
  const canSeeNames = useCan('item.setDecision'); // Leader/Admin only
  const cast = useCastVote();

  if (!item) return <DrawerShell onClose={onClose}><p>Loading…</p></DrawerShell>;

  const myVote = item.votes?.[principalUserId];

  const onConfidence = (c: 'High' | 'Medium' | 'Low') => {
    cast.mutate({ itemId: item.id, partial: { confidence: c } });
  };
  const onTitle = (title: string) => {
    if (title === item.title) return;
    patch.mutate({ id: item.id, patch: { title }, etag: item._etag }, {
      onError: (err) => { if (err instanceof ApiHttpError && err.status === 412) setConflict(true); },
    });
  };
  const onToggleAnchor = () => {
    if (item.isAnchor) {
      setAnchor.mutate({ id: item.id, isAnchor: false, anchorWhy: null, etag: item._etag });
    } else {
      const why = window.prompt('Why is this an anchor?');
      if (!why) return;
      setAnchor.mutate({ id: item.id, isAnchor: true, anchorWhy: why, etag: item._etag });
    }
  };
  const onArchive = () => {
    archive.mutate({ id: item.id, archive: true, etag: item._etag });
    onClose();
  };

  return (
    <DrawerShell onClose={onClose}>
      <AnchorReferencePanel allItems={allItems ?? []} currentItemId={item.id} />
      <div className="mt-4">
        {canEditIntake ? (
          <input
            defaultValue={item.title}
            onBlur={(e) => onTitle(e.target.value.trim())}
            className="w-full rounded border border-slate-200 px-2 py-1 text-lg font-semibold"
          />
        ) : (
          <h2 className="text-lg font-semibold">{item.title}</h2>
        )}
        <p className="mt-1 text-xs text-slate-500">
          Owner: {item.owner?.displayName ?? '—'} · Lifecycle: {item.lifecycle}
        </p>
      </div>
      {conflict && (
        <div className="mt-3"><ConflictBanner onReload={() => { refetch(); setConflict(false); }} onDismiss={() => setConflict(false)} /></div>
      )}
      {canScore && (
        <div className="mt-4">
          <ScoringBlocks item={item} currentUserId={principalUserId} canSeeNames={canSeeNames} />
        </div>
      )}
      {canScore && (
        <div className="mt-4">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-semibold text-slate-700">Confidence</h3>
            <DefinitionPopover definition={getDefinition('confidence')!} />
          </div>
          <div className="mt-1 flex gap-1">
            {(['High', 'Medium', 'Low'] as const).map((c) => {
              const active = myVote?.confidence === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onConfidence(c)}
                  className={`rounded border px-2 py-1 text-xs ${
                    active ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <div className="mt-1">
            <VoteDistribution dimension="confidence" votes={Object.values(item.votes ?? {})} canSeeNames={canSeeNames} />
          </div>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        {canFlagAnchor && (
          <button type="button" onClick={onToggleAnchor} className="text-xs text-amber-700 underline">
            {item.isAnchor ? 'Unflag anchor' : 'Flag as anchor'}
          </button>
        )}
        {canArchive && (
          <button type="button" onClick={onArchive} className="text-xs text-red-700 underline">
            Archive
          </button>
        )}
      </div>
      {canSetDecision && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-700">Decision</h3>
          <select
            value={item.decision ?? ''}
            onChange={(e) => {
              const next = (e.target.value || null) as Decision | null;
              rank.mutate({
                itemId: item.id,
                ifMatch: item._etag,
                targetColumn: next,
                beforeRank: null,
                afterRank: null,
              });
            }}
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">— None (Inbox) —</option>
            <option value="Now">Now</option>
            <option value="Next">Next</option>
            <option value="Later">Later</option>
            <option value="Parked">Parked</option>
          </select>
        </div>
      )}
      <div className="mt-6 border-t border-slate-200 pt-3">
        <button type="button" onClick={() => setActivityOpen((v) => !v)} className="text-xs font-medium text-slate-700">
          Activity {activityOpen ? '▾' : '▸'}
        </button>
        <div className="mt-2">
          <ActivityTimeline itemId={item.id} expanded={activityOpen} />
        </div>
      </div>
    </DrawerShell>
  );
}

function CreateDrawer({ onClose }: { onClose: () => void }) {
  const create = useCreateItem();
  const { data: categories } = useCategories();
  const active = (categories ?? []).filter((c) => !c.archived);
  const defaultCategoryId = active[0]?.id ?? 'cat-product';
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<string>(defaultCategoryId);
  // If categories load after this drawer mounts, adopt the first active id
  // (but only if the user hasn't already changed the select away from the
  // initial fallback). React Query is an external store from React's POV; the
  // alternative (useSyncExternalStore) is overkill for this rare path.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (active.length > 0 && categoryId === 'cat-product' && active[0]!.id !== 'cat-product') {
      setCategoryId(active[0]!.id);
    }
  }, [active.length]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  const [problem, setProblem] = useState('');
  const [outcome, setOutcome] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [impactedUsers, setImpactedUsers] = useState('');
  const [frequencyVolume, setFrequencyVolume] = useState('');
  const [costOfDelayNotes, setCostOfDelayNotes] = useState('');
  const [enablementNotes, setEnablementNotes] = useState('');
  const [evidenceAvailable, setEvidenceAvailable] = useState('');
  const [scopeNotes, setScopeNotes] = useState('');
  const [requestingTeam, setRequestingTeam] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        categoryId,
        problemStatement: problem,
        desiredOutcome: outcome,
        impactedUsers: impactedUsers || null,
        frequencyVolume: frequencyVolume || null,
        costOfDelayNotes: costOfDelayNotes || null,
        enablementNotes: enablementNotes || null,
        evidenceAvailable: evidenceAvailable || null,
        scopeNotes: scopeNotes || null,
        requestingTeam: requestingTeam || null,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <DrawerShell onClose={onClose}>
      <h2 className="text-lg font-semibold">New item</h2>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} required className="rounded border border-slate-300 px-2 py-1 text-sm" />
        </Field>
        <Field label="Category">
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm">
            {active.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Problem statement">
          <textarea value={problem} onChange={(e) => setProblem(e.target.value)} rows={3} className="rounded border border-slate-300 px-2 py-1 text-sm" />
        </Field>
        <Field label="Desired outcome">
          <textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={3} className="rounded border border-slate-300 px-2 py-1 text-sm" />
        </Field>
        <button type="button" onClick={() => setMoreOpen((v) => !v)} className="text-left text-xs font-medium text-slate-600">
          More details {moreOpen ? '▾' : '▸'}
        </button>
        {moreOpen && (
          <div className="flex flex-col gap-3 rounded border border-slate-200 bg-slate-50 p-2">
            <Field label="Impacted users">
              <textarea value={impactedUsers} onChange={(e) => setImpactedUsers(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Frequency / volume">
              <textarea value={frequencyVolume} onChange={(e) => setFrequencyVolume(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Cost of delay notes">
              <textarea value={costOfDelayNotes} onChange={(e) => setCostOfDelayNotes(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Enablement notes">
              <textarea value={enablementNotes} onChange={(e) => setEnablementNotes(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Evidence available">
              <textarea value={evidenceAvailable} onChange={(e) => setEvidenceAvailable(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Scope notes">
              <textarea value={scopeNotes} onChange={(e) => setScopeNotes(e.target.value)} rows={2} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
            <Field label="Requesting team">
              <input value={requestingTeam} onChange={(e) => setRequestingTeam(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Field>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border border-slate-300 px-3 py-1 text-xs">Cancel</button>
          <button type="submit" disabled={create.isPending} className="rounded bg-brand px-3 py-1 text-xs text-white">
            {create.isPending ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function DrawerShell({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <button type="button" aria-label="close drawer" onClick={onClose} className="flex-1 bg-black/30" />
      <aside className="flex h-full w-[480px] flex-col overflow-y-auto border-l border-slate-200 bg-white p-4 shadow-xl">
        <button type="button" onClick={onClose} className="self-end text-xs text-slate-500">Close ✕</button>
        {children}
      </aside>
    </div>
  );
}
